import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { zIndexVoxel } from '../../../logic/zIndexVoxel';
import VoxelInput, { VoxelInputProps } from './VoxelInput';
import { useDebounceValue } from '../../../logic/otherUtilities/useDebounceValue';
import VoxelSelectItem from './VoxelSelectItem';

// Like an autocomplete, but it's a text field - not a select
export interface VoxelComboBoxProps extends Omit<VoxelInputProps, 'onChange'> {
  options?: string[];
  setValue: (value: string) => void;
  maxHeightOptions?: number;
}

export const VoxelComboBox: React.FC<VoxelComboBoxProps> = ({
  options,
  setValue,
  maxHeightOptions = '300',
  ...inputProps
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const listRef = useRef<Array<HTMLElement | null>>([]);

  const { refs, floatingStyles, context } = useFloating<HTMLInputElement>({
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(8),
      flip({ padding: 10 }),
      size({
        apply({ rects, availableHeight, elements }: any) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width + 2}px`,
            maxHeight: `${maxHeightOptions}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const role = useRole(context, { role: 'listbox' });
  const dismiss = useDismiss(context);
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    role,
    dismiss,
    listNav,
  ]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValue(value);

    if (value) {
      setOpen(true);
      setActiveIndex(0);
    } else {
      setOpen(false);
    }
  };

  const debouncedValue = useDebounceValue(inputProps.value ?? '', 100);
  const filteredOptions = useMemo(
    () =>
      options.filter((item) =>
        item.toLowerCase().includes(debouncedValue?.toString()?.toLocaleLowerCase())
      ),
    [options, debouncedValue]
  );

  return (
    <>
      {/* Input element */}
      <VoxelInput
        {...inputProps}
        {...getReferenceProps({
          ref: refs.setReference,
          onChange,
          onKeyDown(event) {
            if (event.key === 'Enter' && activeIndex != null && filteredOptions[activeIndex]) {
              event.preventDefault();
              event.stopPropagation();
              setValue(filteredOptions[activeIndex]);
              setActiveIndex(null);
              setOpen(false);
            }
          },
        })}
      />

      {/* Options */}
      <FloatingPortal>
        {open && !!filteredOptions.length && (
          <FloatingFocusManager context={context} initialFocus={-1} visuallyHiddenDismiss>
            <div
              {...getFloatingProps({
                ref: refs.setFloating,
                style: floatingStyles,
                className: `box-border shadow-lg bg-white rounded p-1 ring-1 ring-voxel-gray-200 overflow-y-auto ${zIndexVoxel.selectContainer}`,
              })}
            >
              {filteredOptions.map((option, index) => (
                <VoxelSelectItem
                  {...getItemProps({
                    key: option,
                    ref(node) {
                      listRef.current[index] = node;
                    },
                    onClick() {
                      setValue(option);
                      setOpen(false);
                      refs.domReference.current?.focus();
                    },
                  })}
                  active={activeIndex === index}
                  selected={inputProps.value === option}
                >
                  {option}
                </VoxelSelectItem>
              ))}
            </div>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  );
};

export default VoxelComboBox;
